import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX, Zap, ShieldCheck, Boxes, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const MICK_IMG = "/mick-hero.jpg";
const AOX_LEDGER_URL = "https://hoodies-marketplace.preview.emergentagent.com/#ledger";
const HERO_VIDEO = "/videos/mick-sentinel.mp4";
const HERO_WEBM = "/videos/mick-sentinel.webm";
const HERO_POSTER = "/videos/poster-sentinel.jpg";

export function Hero() {
  const { user, setAuthOpen } = useAuth();
  const [muted, setMuted] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [latest, setLatest] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/blockchain/blocks");
        if (alive && data.blocks?.length) setLatest(data.blocks[0]);
      } catch {}
    };
    load();
    const t = setInterval(load, 6000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const cta = () => {
    if (user) document.getElementById("mining")?.scrollIntoView({ behavior: "smooth" });
    else setAuthOpen(true);
  };

  return (
    <section
      id="welcome"
      className="relative min-h-screen flex items-center overflow-hidden cyber-grid pt-24 pb-16"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-aox-bg/40 via-transparent to-aox-bg pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-aox-cyan/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-aox-green/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center w-full">
        {/* left copy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 border border-aox-green/40 bg-aox-green/10 px-3 py-1.5 rounded-full mb-6">
            <span className="w-2 h-2 rounded-full bg-aox-green pulse-dot" />
            <span className="font-mono-aox text-[11px] uppercase tracking-[0.25em] text-aox-green">
              Super AI Bot // Online 24/7
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white mb-5">
            MEET <span className="text-aox-cyan text-glow-cyan">AI MICK</span>
            <br />
            YOUR AOX BLOCKCHAIN
            <br />
            <span className="text-aox-green text-glow-green">HOODIE NODE</span>
          </h1>

          <p className="text-base sm:text-lg text-slate-300 max-w-lg leading-relaxed mb-8">
            Kia ora, hello world. I'm Mick — the metadata sentinel guarding the
            AOX ledger. Chat with me, watch the chain breathe in real time, and
            become a hoodie to start mining AOX Bucks.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={cta}
              className="flex items-center gap-2 bg-aox-cyan text-aox-bg font-bold px-7 py-3.5 clip-chamfer hover:-translate-y-0.5 hover:glow-cyan transition-all"
              data-testid="hero-become-hoodie-btn"
            >
              <Zap size={18} />
              {user ? "Open Mining Rig" : "Become a Hoodie & Mine AOX"}
            </button>
            <button
              onClick={() =>
                document.getElementById("chat")?.scrollIntoView({ behavior: "smooth" })
              }
              className="flex items-center gap-2 border border-aox-cyan/40 text-aox-cyan font-semibold px-7 py-3.5 clip-chamfer hover:bg-aox-cyan/10 transition-all"
              data-testid="hero-talk-to-mick-btn"
            >
              Talk to Mick
            </button>
            <a
              href={AOX_LEDGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border border-aox-green/40 text-aox-green font-semibold px-7 py-3.5 clip-chamfer hover:bg-aox-green/10 hover:-translate-y-0.5 transition-all"
              data-testid="hero-aox-ledger-btn"
            >
              Enter AOX Ledger
              <ExternalLink size={16} />
            </a>
          </div>

          <div className="flex flex-wrap gap-6 mt-10">
            <Stat icon={ShieldCheck} label="Verified Nodes" value="99.99%" />
            <Stat icon={Boxes} label="Live Block" value={latest ? `#${latest.height}` : "…"} />
            <Stat icon={Zap} label="Uptime" value="24/7" />
          </div>
        </motion.div>

        {/* right visual */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <div className="relative clip-chamfer border border-aox-cyan/30 bg-aox-card overflow-hidden glow-cyan scanline">
            {showVideo ? (
              <video
                ref={videoRef}
                poster={HERO_POSTER}
                autoPlay
                loop
                muted={muted}
                playsInline
                onError={() => setShowVideo(false)}
                className="w-full h-[420px] sm:h-[520px] object-cover"
                data-testid="hero-video"
              >
                <source src={HERO_WEBM} type="video/webm" />
                <source src={HERO_VIDEO} type="video/mp4" />
              </video>
            ) : (
              <img
                src={MICK_IMG}
                alt="AI Mick"
                className="w-full h-[420px] sm:h-[520px] object-cover object-top"
                data-testid="hero-mick-image"
              />
            )}

            {/* overlay hud */}
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-aox-bg/70 backdrop-blur border border-aox-green/30 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-aox-green pulse-dot" />
              <span className="font-mono-aox text-[10px] uppercase tracking-widest text-aox-green">
                Super AI Bot
              </span>
            </div>

            <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-aox-bg via-aox-bg/70 to-transparent flex items-end justify-between">
              <div>
                <div className="font-display font-bold text-white text-lg">AI MICK</div>
                <div className="font-mono-aox text-[10px] text-aox-cyan uppercase tracking-widest">
                  Metadata Sentinel · AOX
                </div>
              </div>
              <div className="flex gap-2">
                {showVideo && (
                  <button
                    onClick={() => setMuted(!muted)}
                    className="p-2.5 bg-aox-bg/80 border border-aox-cyan/30 rounded-full text-aox-cyan hover:glow-cyan transition-all"
                    data-testid="hero-mute-toggle"
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                )}
                <button
                  onClick={() => setShowVideo(!showVideo)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-aox-cyan text-aox-bg font-bold text-sm rounded-full hover:glow-cyan transition-all"
                  data-testid="hero-play-video-btn"
                >
                  <Play size={14} />
                  {showVideo ? "Show Portrait" : "Play Welcome"}
                </button>
              </div>
            </div>
          </div>

          {/* floating hash block */}
          {latest && (
            <motion.div
              className="absolute -bottom-24 -left-3 sm:-left-8 z-20 bg-aox-elevated border border-aox-green/40 clip-chamfer p-3.5 w-60 glow-green"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              data-testid="hero-hash-block"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-mono-aox text-[10px] uppercase tracking-widest text-aox-green">
                  Latest Hash Block
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-aox-green pulse-dot" />
              </div>
              <div className="font-mono-aox text-aox-cyan text-sm font-bold">
                #{latest.height}
              </div>
              <div className="font-mono-aox text-[10px] text-slate-400 truncate mt-1">
                {latest.hash}
              </div>
              <div className="font-mono-aox text-[10px] text-slate-500 mt-1">
                {latest.tx_count} txns · +{latest.aox_reward} AOX
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon size={18} className="text-aox-cyan" />
      <div>
        <div className="font-mono-aox text-sm font-bold text-white">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
    </div>
  );
}
